<template>
  <div class="w-full mx-auto">
    <form @submit.prevent="handleLogin" class="space-y-4 sm:space-y-6">
      <div class="text-center mb-6">
        <h2 class="text-2xl sm:text-3xl font-bold text-gray-900 font-['Raleway']">Sign in to your account</h2>
        <p class="mt-3 text-sm text-gray-600">
          Or
          <button 
            type="button"
            @click="$emit('switch-mode', 'register')"
            class="font-medium text-[#4ECF84] hover:text-[#4A9B7A] font-['Raleway']"
          >
            create a new account
          </button>
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="authStore.error" class="rounded-md bg-red-50 p-4 my-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">{{ authStore.error }}</h3>
          </div>
        </div>
      </div>

      <!-- Email Field -->
      <div>
        <label for="email" class="block text-sm font-medium text-gray-700 mb-2 font-['Raleway']">
          Email address
        </label>
        <div>
          <input
            id="email"
            v-model="form.email"
            name="email"
            type="email"
            autocomplete="email"
            required
            :class="[
              'appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#4ECF84] focus:border-[#4ECF84] text-base font-[\'Raleway\']',
              errors.email ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Enter your email"
          />
          <p v-if="errors.email" class="mt-2 text-sm text-red-600">{{ errors.email }}</p>
        </div>
      </div>

      <!-- Password Field -->
      <div>
        <label for="password" class="block text-sm font-medium text-gray-700 mb-2 font-['Raleway']">
          Password
        </label>
        <div class="relative">
          <input
            id="password"
            v-model="form.password"
            name="password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="current-password"
            required
            :class="[
              'appearance-none block w-full px-4 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#4ECF84] focus:border-[#4ECF84] text-base pr-12 font-[\'Raleway\']',
              errors.password ? 'border-red-300' : 'border-gray-300'
            ]"
            placeholder="Enter your password"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon 
              :name="showPassword ? 'eye-off' : 'eye'" 
              size="sm"
            />
          </button>
          <p v-if="errors.password" class="mt-2 text-sm text-red-600">{{ errors.password }}</p>
        </div>
      </div>

      <!-- Remember Me & Forgot Password -->
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <input
            id="remember-me"
            v-model="form.rememberMe"
            name="remember-me"
            type="checkbox"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label for="remember-me" class="ml-2 block text-sm text-gray-900">
            Remember me
          </label>
        </div>

        <div class="text-sm">
          <button
            type="button"
            @click="$emit('switch-mode', 'forgot-password')"
            class="font-medium text-blue-600 hover:text-blue-500"
          >
            Forgot your password?
          </button>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="pt-2">
        <button
          type="submit"
          :disabled="authStore.isLoading || !isFormValid"
          class="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-medium rounded-lg text-white bg-[#4ECF84] hover:bg-[#4A9B7A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4ECF84] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-['Raleway']"
        >
          <span v-if="authStore.isLoading" class="absolute left-0 inset-y-0 flex items-center pl-4">
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          {{ authStore.isLoading ? 'Signing in...' : 'Sign in' }}
        </button>
      </div>
    </form>
    
    <!-- Email Verification Prompt -->
    <EmailVerificationPrompt
      v-if="showEmailVerification"
      :user-email="form.email"
      :show="showEmailVerification"
      @verified="handleEmailVerified"
      @logout="handleLogout"
      @close="showEmailVerification = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import EmailVerificationPrompt from './EmailVerificationPrompt.vue'
import { toastManager } from '@/composables/useToasts.js'
import Icon from '@/components/Icon.vue'

const emit = defineEmits(['switch-mode', 'login-success'])
const router = useRouter()
const authStore = useAuthStore()

// Form state
const form = ref({
  email: '',
  password: '',
  rememberMe: false
})

const showPassword = ref(false)
const errors = ref({})
const showEmailVerification = ref(false)

// Computed
const isFormValid = computed(() => {
  return form.value.email && 
         form.value.password && 
         form.value.email.includes('@') &&
         form.value.password.length >= 6
})

// Methods
const validateForm = () => {
  errors.value = {}
  
  if (!form.value.email) {
    errors.value.email = 'Email is required'
  } else if (!form.value.email.includes('@')) {
    errors.value.email = 'Please enter a valid email address'
  }
  
  if (!form.value.password) {
    errors.value.password = 'Password is required'
  } else if (form.value.password.length < 6) {
    errors.value.password = 'Password must be at least 6 characters'
  }
  
  return Object.keys(errors.value).length === 0
}

const handleLogin = async () => {
  // Clear previous errors
  authStore.clearError()
  
  if (!validateForm()) {
    return
  }

  const result = await authStore.login({
    email: form.value.email,
    password: form.value.password,
    rememberMe: form.value.rememberMe
  })

  if (result.success) {
    console.log('Login successful in LoginForm, emitting login-success');
    
    // Refresh currency store with user's profile currency
    try {
      const { useCurrencyStore } = await import('@/store/currency')
      const currencyStore = useCurrencyStore()
      await currencyStore.initialize(authStore)
      console.log(`💱 Currency store refreshed after login with profile currency: ${authStore.user?.currency || 'default'}`)
    } catch (err) {
      console.warn('Failed to refresh currency store after login:', err)
    }
    
    emit('login-success')
    // Navigation is handled by the parent (AuthModal)
  } else if (result.error && result.error.includes('verify your email')) {
    if (showEmailVerification.value) {
      // User already clicked "I've verified" but email still isn't confirmed in the backend
      toastManager.info('Not verified yet', 'Please check your inbox and click the verification link, then try again.')
    }
    showEmailVerification.value = true
  }
}

// Clear errors when user starts typing
const clearFieldError = (field) => {
  if (errors.value[field]) {
    delete errors.value[field]
  }
}

// Email verification handlers
const handleEmailVerified = async () => {
  showEmailVerification.value = false
  // Retry login silently — success toast fires from the normal login success path.
  // Showing a premature "Email Verified!" toast here causes a false positive if the
  // email hasn't actually been verified yet, which re-opens the modal immediately after.
  await handleLogin()
}

const handleLogout = () => {
  showEmailVerification.value = false
  form.value.password = '' // Clear password for security
  authStore.clearError()
  toastManager.info('Signed Out', 'Please sign in again when you\'re ready.')
}

onMounted(() => {
  // Focus email field
  const emailInput = document.getElementById('email')
  if (emailInput) {
    emailInput.focus()
  }
})
</script>